import { produce } from "immer";
import { GlobalActions, resetAction } from "../../globals/store/actions";
import { createEntityAdapter } from "../../helpers/store/create-entity-adapter";
import {
  SupportChatAppearance,
  SupportConversation,
  SupportMessage,
  SupportOnlineAgent,
  SupportVisitor,
} from "../interfaces/types";
import {
  addSupportMessageAction,
  setSupportConfigAction,
  setSupportConversationsAction,
  setSupportMessagesAction,
  setSupportVisitorsAction,
  setSelectedSupportConversationAction,
  setWidgetSessionAction,
  setSupportOnlineAgentsAction,
  setSupportAgentSocketSubscriptionAction,
  upsertSupportConversationAction,
  upsertSupportVisitorAction,
  removeSupportVisitorAction,
  WembiiSupportAction,
} from "./actions";

export interface WembiiSupportState {
  config: SupportChatAppearance | null;
  conversations: {
    ids: string[];
    entities: Record<string, SupportConversation>;
  };
  selected: SupportConversation | null;
  messages: {
    ids: string[];
    entities: Record<string, SupportMessage>;
  };
  visitors: {
    ids: string[];
    entities: Record<string, SupportVisitor>;
  };
  widgetSession: {
    conversationId: string | null;
    room: string | null;
  };
  onlineAgents: SupportOnlineAgent[];
  agentSocketSubscription: {
    userId: string;
    conversationId?: string;
    room?: string;
  } | null;
}

const initialState: WembiiSupportState = {
  config: null,
  conversations: { ids: [], entities: {} },
  selected: null,
  messages: { ids: [], entities: {} },
  visitors: { ids: [], entities: {} },
  widgetSession: { conversationId: null, room: null },
  onlineAgents: [],
  agentSocketSubscription: null,
};

const conversationsAdapter = createEntityAdapter<SupportConversation>(
  (c) => c.id
);
const messagesAdapter = createEntityAdapter<SupportMessage>((m) => m.id);
const visitorsAdapter = createEntityAdapter<SupportVisitor>((v) => v.id);

export default produce(
  (
    draft: WembiiSupportState = initialState,
    action: WembiiSupportAction | GlobalActions
  ) => {
    switch (action.type) {
      case setSupportConfigAction.type:
        draft.config = action.config;
        return draft;

      case setSupportConversationsAction.type:
        conversationsAdapter.addMany(action.conversations, draft.conversations);
        return draft;

      case upsertSupportConversationAction.type: {
        const conversation = {
          ...action.conversation,
          id: String(action.conversation.id),
        };
        const exists = draft.conversations.entities[conversation.id];
        if (exists) {
          conversationsAdapter.updateOne(conversation, draft.conversations);
        } else {
          conversationsAdapter.setOne(conversation, draft.conversations);
        }
        conversationsAdapter.moveToFirstPosition(
          conversation.id,
          draft.conversations
        );
        if (draft.selected?.id === conversation.id) {
          draft.selected = {
            ...draft.selected,
            ...conversation,
          };
        }
        return draft;
      }

      case setSelectedSupportConversationAction.type:
        if (!action.id) {
          draft.selected = null;
        } else {
          draft.selected =
            draft.conversations.entities[action.id] ??
            ({ id: String(action.id) } as SupportConversation);
        }
        messagesAdapter.removeAll(draft.messages);
        return draft;

      case setSupportMessagesAction.type:
        messagesAdapter.removeAll(draft.messages);
        messagesAdapter.addMany(action.messages, draft.messages);
        return draft;

      case addSupportMessageAction.type: {
        if (!draft.messages.entities[action.message.id]) {
          messagesAdapter.setOne(action.message, draft.messages);
        }
        const conv = draft.conversations.entities[action.conversationId];
        if (conv) {
          conv.snippet = action.message.content;
          conv.date = "Hoy";
        }
        return draft;
      }

      case setSupportVisitorsAction.type:
        visitorsAdapter.removeAll(draft.visitors);
        visitorsAdapter.addMany(action.visitors, draft.visitors);
        return draft;

      case upsertSupportVisitorAction.type: {
        const visitor = {
          ...action.visitor,
          id: String(action.visitor.id),
          userId: action.visitor.userId
            ? String(action.visitor.userId)
            : String(action.visitor.id),
        };
        const exists = draft.visitors.entities[visitor.id];
        if (exists) {
          visitorsAdapter.updateOne(visitor, draft.visitors);
        } else {
          visitorsAdapter.setOne(visitor, draft.visitors);
        }

        const visitorConversationId = visitor.conversationId
          ? String(visitor.conversationId)
          : null;
        const isOnline = visitor.status === "online";
        if (visitorConversationId) {
          const conversation = draft.conversations.entities[visitorConversationId];
          if (conversation) {
            conversation.isOnline = isOnline;
            conversation.session = {
              ...(conversation.session ?? {}),
              isOnline,
            };
            if (draft.selected?.id === conversation.id) {
              draft.selected = {
                ...draft.selected,
                isOnline,
                session: {
                  ...(draft.selected.session ?? {}),
                  isOnline,
                },
              };
            }
          }
        }
        return draft;
      }

      case removeSupportVisitorAction.type: {
        const visitorId = String(action.id);
        const visitor = draft.visitors.entities[visitorId];
        visitorsAdapter.removeOne(visitorId, draft.visitors);

        const visitorConversationId = visitor?.conversationId
          ? String(visitor.conversationId)
          : null;
        if (visitorConversationId) {
          const conversation = draft.conversations.entities[visitorConversationId];
          if (conversation) {
            conversation.isOnline = false;
            conversation.session = {
              ...(conversation.session ?? {}),
              isOnline: false,
            };
            if (draft.selected?.id === conversation.id) {
              draft.selected = {
                ...draft.selected,
                isOnline: false,
                session: {
                  ...(draft.selected.session ?? {}),
                  isOnline: false,
                },
              };
            }
          }
        }
        return draft;
      }

      case setWidgetSessionAction.type:
        draft.widgetSession = {
          conversationId: action.conversationId,
          room: action.room,
        };
        return draft;

      case setSupportOnlineAgentsAction.type:
        draft.onlineAgents = action.agents;
        return draft;

      case setSupportAgentSocketSubscriptionAction.type:
        draft.agentSocketSubscription = action.userId
          ? {
              userId: action.userId,
              conversationId: action.conversationId,
              room: action.room,
            }
          : null;
        return draft;

      case resetAction.type:
        return initialState;

      case resetAction.type:
        return initialState;

      default:
        return draft;
    }
  }
);
